/**
 * The few things the offline layer has to tell the interface about, without either side holding
 * a reference to the other: the queue changed, a copy was served from the device, or the sync
 * engine moved.
 */

export type OfflineEvent =
  /** A write was queued, sent, refused or discarded. */
  | 'queue-changed'
  /** The sync engine started, finished, or paused. */
  | 'sync-changed'
  /** A read was answered from the device copy rather than from the server. */
  | 'served-offline'
  /** The device vault was enrolled, unlocked, sealed or forgotten. */
  | 'vault-changed';

type Listener = () => void;

const listeners = new Map<OfflineEvent, Set<Listener>>();

export function subscribeToOffline(event: OfflineEvent, listener: Listener): () => void {
  const bucket = listeners.get(event) ?? new Set<Listener>();
  bucket.add(listener);
  listeners.set(event, bucket);
  return () => {
    bucket.delete(listener);
  };
}

export function emitOffline(event: OfflineEvent): void {
  for (const listener of listeners.get(event) ?? []) listener();
}

/** The timestamp of the most recent copy served from the device, for the offline notice. */
let lastServedOfflineAt: string | null = null;

export function noteServedOffline(storedAt: string): void {
  lastServedOfflineAt = storedAt;
  emitOffline('served-offline');
}

export function lastOfflineCopyAt(): string | null {
  return lastServedOfflineAt;
}

export function resetOfflineEvents(): void {
  listeners.clear();
  lastServedOfflineAt = null;
}
